import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output, resource, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { findExceptableTaskEvidence, Task, TASK_EVIDENCE_SECTION_LABELS, TaskEvidenceKind } from '@jms/model';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { ToastService } from '@workbuddy/ui/services';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { ButtonAccent, ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { SelectComponent } from '@workbuddy/ui/v2/fields/select';
import { explainedEvidenceKinds, findExplainedTaskEvidence, findUnexceptableTaskEvidence, offerEvidenceExceptionReasons } from '../evidence.exception.policy';
import { TaskEvidenceExceptionService } from '../task.evidence.exception.service';
import { taskEvidenceExceptionDialogBridgeStyles, taskEvidenceExceptionDialogStyleId } from './task.evidence.exception.dialog.theme';

/** Record, replace and remove in one place (D1); resolves true when the task's exception may have changed, so the caller rereads it. */
@Component({
  selector: 'task-evidence-exception-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [FormsModule, BadgeComponent, ButtonComponent, SelectComponent],
  templateUrl: './task.evidence.exception.dialog.component.html',
  styleUrls: ['./task.evidence.exception.dialog.component.less'],
})
export class TaskEvidenceExceptionDialogComponent {

  private readonly document = inject(DOCUMENT);
  private readonly dialogRef = injectDialogRef<boolean>();
  private readonly exceptions = inject(TaskEvidenceExceptionService);
  private readonly toast = inject(ToastService);
  private readonly generalSetting = inject(GeneralSettingService);
  private readonly dates = new DateTimeFormatPipe();

  public readonly $task = input.required<Task>({ alias: 'task' });
  public readonly $readonly = input(false, { alias: 'readonly' });
  public readonly $timezone = input<string | undefined>(undefined, { alias: 'timezone' });
  /** Lets the opener refuse Escape while a write runs: a dismissal then would lose the refresh a successful write needs. */
  public readonly busyChange = output<boolean>();

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonAccent = ButtonAccent;
  protected readonly BadgeMode = BadgeMode;
  protected readonly dateFormat = `${this.generalSetting.generalSettings?.region?.dateFormat ?? 'dd/MM/yyyy'} ${this.generalSetting.generalSettings?.region?.timeFormat ?? 'HH:mm'}`;
  protected readonly titleId = `task-evidence-exception-title-${crypto.randomUUID()}`;
  protected readonly commentId = `task-evidence-exception-comment-${crypto.randomUUID()}`;
  protected readonly $exception = computed(() => this.$task().evidenceException);
  protected readonly $title = computed(() => this.$readonly() ? 'Evidence exception' : 'Unable to capture');
  protected readonly $recorded = computed(() => {
    const exception = this.$exception();
    return exception ? `Recorded by ${exception.recordedBy?.name ?? ''} · ${this.dates.transform(exception.recordedOn, this.$timezone(), this.dateFormat)}` : '';
  });
  private readonly reasons = resource({
    params: () => this.$readonly() ? undefined : this.$task()._id,
    loader: () => this.exceptions.reasons(),
  });
  protected readonly $reasons = computed(() => offerEvidenceExceptionReasons(this.$task(), this.reasons.hasValue() ? this.reasons.value() : []));
  protected readonly $reasonsLoading = computed(() => this.reasons.isLoading());
  // A failed read would otherwise read as no reason fitting the missing evidence.
  protected readonly $reasonsFailed = computed(() => !!this.reasons.error());
  // Seeded with the current reason, which counts only while it is still offered: the server checks a replacement as a new record (D3).
  protected readonly $reasonId = linkedSignal<string | null>(() => this.$exception()?.reason?._id ?? null);
  protected readonly $comment = linkedSignal(() => this.$exception()?.comment ?? '');
  private readonly $selected = computed(() => this.$reasons().find(tag => tag._id === this.$reasonId()));
  protected readonly $selectedId = computed(() => this.$selected()?._id ?? null);
  // Read only it shows what the stored exception explains; editing, what the chosen reason would explain, or before a choice every missing allow-exceptions evidence (D11).
  private readonly $explainedKinds = computed(() => {
    if (this.$readonly()) return findExplainedTaskEvidence(this.$task());
    const tag = this.$selected();
    return tag ? explainedEvidenceKinds(this.$task(), tag) : findExceptableTaskEvidence(this.$task());
  });
  protected readonly $explained = computed(() => labels(this.$explainedKinds()));
  // Only what is true is said: a kind the chosen reason leaves out, and evidence no exception can explain.
  protected readonly $notes = computed(() => {
    if (this.$readonly()) return [];
    const explained = this.$explainedKinds();
    const uncovered = this.$selected() ? findExceptableTaskEvidence(this.$task()).filter(kind => !explained.includes(kind)) : [];
    return [
      ...uncovered.map(kind => `${TASK_EVIDENCE_SECTION_LABELS[kind]} stays Outstanding: this reason does not cover it.`),
      ...findUnexceptableTaskEvidence(this.$task()).map(kind => `${TASK_EVIDENCE_SECTION_LABELS[kind]} must still be captured.`),
    ];
  });
  // An empty picker would leave the user guessing; reasons are tags an administrator sets up.
  protected readonly $noReasonFits = computed(() => this.reasons.hasValue() && !this.$reasons().length ? describeNoReasonFits(findExceptableTaskEvidence(this.$task())) : '');
  protected readonly $busy = signal(false);
  protected readonly $error = signal<string | null>(null);
  protected readonly $canSave = computed(() => !this.$readonly() && !!this.$selected() && !this.$busy());

  public constructor() {

    DesignSystem.Mount(this.document, taskEvidenceExceptionDialogStyleId, taskEvidenceExceptionDialogBridgeStyles);
  }

  protected onReason(reasonId: string | null): void {

    this.$reasonId.set(reasonId);
    this.$error.set(null);
  }

  protected onComment(event: Event): void {

    if (event.target instanceof HTMLTextAreaElement) this.$comment.set(event.target.value);
  }

  protected cancel(): void {

    if (this.$busy()) return;
    this.dialogRef.dismiss();
  }

  protected retryReasons(): void {

    this.reasons.reload();
  }

  protected save(): void {

    const tag = this.$selected();
    if (!tag || !this.$canSave()) return;
    const comment = this.$comment().trim();
    void this.write(() => this.exceptions.record(this.$task()._id, comment ? { reasonId: tag._id, comment } : { reasonId: tag._id }));
  }

  protected remove(): void {

    if (this.$readonly() || this.$busy()) return;
    void this.write(() => this.exceptions.remove(this.$task()._id));
  }

  private async write(send: () => Promise<Task>): Promise<void> {

    this.$busy.set(true);
    this.busyChange.emit(true);
    this.$error.set(null);
    try {
      await send();
      this.dialogRef.close(true);
    } catch (error) {
      // A 409 means the task moved on under the dialog: what it showed is stale, so the page rereads rather than retrying.
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.toast.warning('The task changed meanwhile', serverMessage(error) ?? 'It has been reloaded.');
        this.dialogRef.close(true);
        return;
      }
      this.$error.set(serverMessage(error) ?? 'The exception could not be saved.');
    } finally {
      this.$busy.set(false);
      this.busyChange.emit(false);
    }
  }
}

function labels(kinds: TaskEvidenceKind[]): string[] {

  return kinds.map(kind => TASK_EVIDENCE_SECTION_LABELS[kind]);
}

function serverMessage(error: unknown): string | undefined {

  if (!(error instanceof HttpErrorResponse)) return undefined;
  const body: unknown = error.error;
  const message = body && typeof body === 'object' && 'message' in body ? body.message : undefined;
  if (typeof message === 'string') return message;
  return Array.isArray(message) ? message.join(', ') : undefined;
}

const PLURAL_KINDS: readonly TaskEvidenceKind[] = ['beforePhoto', 'duringPhoto', 'afterPhoto', 'file'];

function describeNoReasonFits(kinds: TaskEvidenceKind[]): string {

  const names = kinds.map(kind => TASK_EVIDENCE_SECTION_LABELS[kind].toLowerCase()).join(' or ');
  const article = kinds.length === 1 && !PLURAL_KINDS.includes(kinds[0]) ? 'a ' : '';

  return `No reason applies to ${article}missing ${names}. An administrator can add one in Settings › Tags (Evidence Exception).`;
}
